import { CLIENT_PATHS, ClientPath } from "src/utils/routing/routes";
import HomePage from "src/pages/HomePage/HomePage";
import HomePageV2 from "src/pages/HomePage/HomePageV2/HomePageV2";
import SelectPartnerPage from "src/pages/SelectPartnerPage/SelectPartnerPage";
import SelectTenantPage from "src/pages/SelectTenantPage/SelectTenantPage";
import SelectReportCategoryPage from "src/pages/SelectReportCategoryPage/SelectReportCategoryPage";
import DownloadReportsPage from "src/pages/DownloadReportsPage/DownloadReportsPage";
import DashboardSelectPartnerPage from "src/pages/DashboardsPages/DashboardSelectPartnerPage";
import DashboardSelectionPage from "src/pages/DashboardsPages/DashboardSelectionPage";
import MyAccountPage from "src/pages/MyAccountPage/MyAccountPage";
import AdminPage from "src/pages/AdminPage/AdminPage";
import ManagePartnersPage from "src/pages/ManagePartnersPage/ManagePartnersPage";
import ManageReportAlertsPage from "../../pages/ManageReportAlertsPage/ManageReportAlertsPage";
import ManageReportCategoriesPage from "src/pages/ManageReportCategoriesPage/ManageReportCategoriesPage";
import ManageFrequencyTypesPage from "src/pages/ManageFrequencyTypesPage/ManageFrequencyTypesPage";
import ManageUsersPage from "src/pages/ManageUsersPage/ManageUsersPage";
import ManageFrequentlyAskedQuestionsPage from "src/pages/ManageFrequentlyAskedQuestionsPage/ManageFrequentlyAskedQuestionsPage";
import CreatePartnerPage from "src/pages/CreatePartnerPage/CreatePartnerPage";
import CreateReportCategoryPage from "src/pages/CreateReportCategoryPage/CreateReportCategoryPage";
import CreateFrequencyTypePage from "src/pages/CreateFrequencyTypePage/CreateFrequencyTypePage";
import CreateUserPage from "src/pages/CreateUserPage/CreateUserPage";
import CreateFrequentlyAskedQuestionPage from "src/pages/CreateFrequentlyAskedQuestionPage/CreateFrequentlyAskedQuestionPage";
import EditPartnerPage from "src/pages/EditPartnerPage/EditPartnerPage";
import EditReportCategoryPage from "src/pages/EditReportCategoryPage/EditReportCategoryPage";
import EditFrequencyTypePage from "src/pages/EditFrequencyTypePage/EditFrequencyTypePage";
import EditUserPage from "src/pages/EditUserPage/EditUserPage";
import EditFrequentlyAskedQuestionPage from "src/pages/EditFrequentlyAskedQuestionPage/EditFrequentlyAskedQuestionPage";
import MarketplacePage from "src/pages/MarketplacePage/MarketplacePage";
import ManageDashboardPage from "src/pages/ManageDashboardPage/ManageDashboardPage";
import ManageSaasUsersPage from "src/pages/ManageSaasUsersPage/ManageSaasUsersPage";
import ManageSaasUserInvitationsPage from "src/pages/ManageSaasUserInvitationsPage/ManageSaasUserInvitationsPage";
import InviteSaasUserPage from "src/pages/InviteSaasUserPage/InviteSaasUserPage";
import EditSaasUserPage from "src/pages/EditSaasUserPage/EditSaasUserPage";
import MarketingSelectPartnerPage from "src/pages/MarketingSelectPartnerPage/MarketingSelectPartnerPage";
import SelectOperationalFormPage from "src/pages/SelectOperationalFormPage/SelectOperationalFormPage";
import CustomerInvoiceFormPage from "src/pages/CustomerInvoiceFormPage/CustomerInvoiceFormPage";
import CreateCustomerInvoicePage from "src/pages/CustomerInvoiceFormPage/CreateCustomerInvoicePage";
import EditCustomerInvoicePage from "src/pages/CustomerInvoiceFormPage/EditCustomerInvoicePage";
import WalmartDisputesFormPage from "src/pages/WalmartDisputesFormPage/WalmartDisputesFormPage";
import CreateWalmartDisputesPage from "src/pages/WalmartDisputesFormPage/CreateWalmartDisputePage";
import EditWalmartDisputesPage from "src/pages/WalmartDisputesFormPage/EditWalmartDisputesPage";
import FrequentlyAskedQuestionsPage from "src/pages/FrequentlyAskedQuestionsPage/FrequentlyAskedQuestionsPage";
import ReportAnIssuePage from "src/pages/ReportAnIssuePage/ReportAnIssuePage";
import LogoutPage from "src/pages/LogoutPage/LogoutPage";
import LoginPage from "src/pages/LoginPage/LoginPage";
import NotAuthorizedPage from "src/pages/NotAuthorizedPage/NotAuthorizedPage";
import { PageMetadata } from "src/utils/routing/PageMetadata";
import { FEATURES } from "src/utils/feature";
import RStudioLiveBoard from "src/containers/rStudio/RStudioLiveBoard";
import QuicksightDashboard from "src/containers/quicksight/QuicksightDashboard";
import QuicksightDashboardV2 from "src/containers/quicksight/QuicksightDashboardV2";
import TableauDashboard from "src/containers/tableau/TableauDashboard";
import ManageMarketingContentPage from "src/pages/ManageMarketingContentPage/ManageMarketingContentPage";
import ManageBannerPage from "src/pages/ManageBannerPage/ManageBannerPage";

type PageMetadatas = Partial<{ [key in ClientPath]: PageMetadata }>;

/**
 * Detects whether the app is running under the devex hostname (e.g. *developer*).
 * Mirrors the existing host check used elsewhere in the codebase
 * (see `src/utils/handleAuthentication.ts` and `src/utils/routing/routes.ts`).
 */
const isOnDevex = (): boolean => {
	if (typeof window === "undefined") {
		return false;
	}
	return window.location.hostname.includes("developer");
};

/**
 * On devex, every legacy `/partnerships/analytics/...` route (everything except
 * the explicit V2 routes that hardcode their paths) is blocked and rendered
 * as `NotAuthorizedPage`. On non-devex hosts, the legacy `Page` component is used.
 */
const legacyOrNotAuthorized = (
	legacyPage: PageMetadata["Page"]
): PageMetadata["Page"] => (isOnDevex() ? NotAuthorizedPage : legacyPage);

const corePages: PageMetadatas = {
	[CLIENT_PATHS.HOME_PAGE]: new PageMetadata(
		HomePage,
		CLIENT_PATHS.HOME_PAGE,
		[FEATURES.NO_FEATURE_REQUIRED],
		{ menuIcon: "Home" }
	),

	[CLIENT_PATHS.HOME_PAGE_V2]: new PageMetadata(
		HomePageV2,
		CLIENT_PATHS.HOME_PAGE_V2,
		[FEATURES.NO_FEATURE_REQUIRED],
		{ menuIcon: "Home" }
	),

	[CLIENT_PATHS.QUICKSIGHT_PAGE_V2]: new PageMetadata(
		QuicksightDashboardV2,
		CLIENT_PATHS.QUICKSIGHT_PAGE_V2,
		[FEATURES.VIEW_QUICKSIGHT_DASHBOARDS],
		{}
	),
	[CLIENT_PATHS.SELECT_PARTNER_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(SelectPartnerPage),
		CLIENT_PATHS.SELECT_PARTNER_PAGE,
		[FEATURES.VIEW_REPORTS],
		{ menuIcon: "Bill" }
	),
	[CLIENT_PATHS.SELECT_TENANT_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(SelectTenantPage),
		CLIENT_PATHS.SELECT_TENANT_PAGE,
		[FEATURES.MANAGE_EXTERNAL_USERS],
		{ menuIcon: "UserMultiuser" }
	),
	[CLIENT_PATHS.SELECT_REPORT_CATEGORY_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(SelectReportCategoryPage),
		CLIENT_PATHS.SELECT_REPORT_CATEGORY_PAGE,
		[FEATURES.VIEW_REPORTS],
		{}
	),
	[CLIENT_PATHS.DOWNLOAD_REPORTS_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(DownloadReportsPage),
		CLIENT_PATHS.DOWNLOAD_REPORTS_PAGE,
		[FEATURES.VIEW_REPORTS],
		{}
	),
	[CLIENT_PATHS.DASHBOARDS_SELECT_PARTNER_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(DashboardSelectPartnerPage),
		CLIENT_PATHS.DASHBOARDS_SELECT_PARTNER_PAGE,
		[FEATURES.NO_FEATURE_REQUIRED],
		{ menuIcon: "Information" }
	),
	[CLIENT_PATHS.DASHBOARDS_SELECT_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(DashboardSelectionPage),
		CLIENT_PATHS.DASHBOARDS_SELECT_PAGE,
		[FEATURES.NO_FEATURE_REQUIRED],
		{}
	),
	[CLIENT_PATHS.RSTUDIO_LIVEBOARD_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(RStudioLiveBoard),
		CLIENT_PATHS.RSTUDIO_LIVEBOARD_PAGE,
		[FEATURES.NO_FEATURE_REQUIRED],
		{}
	),
	[CLIENT_PATHS.QUICKSIGHT_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(QuicksightDashboard),
		CLIENT_PATHS.QUICKSIGHT_PAGE,
		[FEATURES.VIEW_QUICKSIGHT_DASHBOARDS],
		{}
	),
	[CLIENT_PATHS.TABLEAU_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(TableauDashboard),
		CLIENT_PATHS.TABLEAU_PAGE,
		[FEATURES.NO_FEATURE_REQUIRED],
		{}
	),
	[CLIENT_PATHS.MY_ACCOUNT_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(MyAccountPage),
		CLIENT_PATHS.MY_ACCOUNT_PAGE,
		[FEATURES.NO_FEATURE_REQUIRED],
		{ menuIcon: "User" }
	)
};

const adminPages: PageMetadatas = {
	[CLIENT_PATHS.ADMIN_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(AdminPage),
		CLIENT_PATHS.ADMIN_PAGE,
		[FEATURES.INTERNAL_ADMIN],
		{ menuIcon: "Wrench" }
	),
	[CLIENT_PATHS.MANAGE_PARTNERS_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(ManagePartnersPage),
		CLIENT_PATHS.MANAGE_PARTNERS_PAGE,
		[FEATURES.MANAGE_PARTNERS],
		{ menuIcon: "Card" }
	),
	[CLIENT_PATHS.MANAGE_REPORT_ALERTS_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(ManageReportAlertsPage),
		CLIENT_PATHS.MANAGE_REPORT_ALERTS_PAGE,
		[FEATURES.MANAGE_REPORT_CATEGORIES],
		{ menuIcon: "ViewGrid" }
	),
	[CLIENT_PATHS.CREATE_PARTNER_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(CreatePartnerPage),
		CLIENT_PATHS.CREATE_PARTNER_PAGE,
		[FEATURES.MANAGE_PARTNERS],
		{}
	),
	[CLIENT_PATHS.EDIT_PARTNER_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(EditPartnerPage),
		CLIENT_PATHS.EDIT_PARTNER_PAGE,
		[FEATURES.MANAGE_PARTNERS],
		{}
	),

	[CLIENT_PATHS.MANAGE_REPORT_CATEGORIES_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(ManageReportCategoriesPage),
		CLIENT_PATHS.MANAGE_REPORT_CATEGORIES_PAGE,
		[FEATURES.MANAGE_REPORT_CATEGORIES],
		{ menuIcon: "ViewGrid" }
	),
	[CLIENT_PATHS.CREATE_REPORT_CATEGORY_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(CreateReportCategoryPage),
		CLIENT_PATHS.CREATE_REPORT_CATEGORY_PAGE,
		[FEATURES.MANAGE_REPORT_CATEGORIES],
		{}
	),
	[CLIENT_PATHS.EDIT_REPORT_CATEGORY_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(EditReportCategoryPage),
		CLIENT_PATHS.EDIT_REPORT_CATEGORY_PAGE,
		[FEATURES.MANAGE_REPORT_CATEGORIES],
		{}
	),

	[CLIENT_PATHS.MANAGE_FREQUENCY_TYPES_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(ManageFrequencyTypesPage),
		CLIENT_PATHS.MANAGE_FREQUENCY_TYPES_PAGE,
		[FEATURES.MANAGE_REPORT_CATEGORIES],
		{ menuIcon: "Calendar" }
	),
	[CLIENT_PATHS.CREATE_FREQUENCY_TYPE_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(CreateFrequencyTypePage),
		CLIENT_PATHS.CREATE_FREQUENCY_TYPE_PAGE,
		[FEATURES.MANAGE_REPORT_CATEGORIES],
		{}
	),
	[CLIENT_PATHS.EDIT_FREQUENCY_TYPE_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(EditFrequencyTypePage),
		CLIENT_PATHS.EDIT_FREQUENCY_TYPE_PAGE,
		[FEATURES.MANAGE_REPORT_CATEGORIES],
		{}
	),

	[CLIENT_PATHS.MANAGE_EXTERNAL_USERS_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(ManageUsersPage),
		CLIENT_PATHS.MANAGE_EXTERNAL_USERS_PAGE,
		[FEATURES.INTERNAL_ADMIN],
		{ menuIcon: "UserMultiuser" }
	),
	[CLIENT_PATHS.CREATE_USER_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(CreateUserPage),
		CLIENT_PATHS.CREATE_USER_PAGE,
		[FEATURES.INTERNAL_ADMIN],
		{}
	),
	[CLIENT_PATHS.EDIT_USER_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(EditUserPage),
		CLIENT_PATHS.EDIT_USER_PAGE,
		[FEATURES.INTERNAL_ADMIN],
		{}
	),

	[CLIENT_PATHS.MANAGE_FREQUENTLY_ASKED_QUESTIONS_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(ManageFrequentlyAskedQuestionsPage),
		CLIENT_PATHS.MANAGE_FREQUENTLY_ASKED_QUESTIONS_PAGE,
		[FEATURES.INTERNAL_ADMIN],
		{ menuIcon: "Question" }
	),
	[CLIENT_PATHS.CREATE_FREQUENTLY_ASKED_QUESTION_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(CreateFrequentlyAskedQuestionPage),
		CLIENT_PATHS.CREATE_FREQUENTLY_ASKED_QUESTION_PAGE,
		[FEATURES.INTERNAL_ADMIN],
		{}
	),
	[CLIENT_PATHS.EDIT_FREQUENTLY_ASKED_QUESTION_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(EditFrequentlyAskedQuestionPage),
		CLIENT_PATHS.EDIT_FREQUENTLY_ASKED_QUESTION_PAGE,
		[FEATURES.INTERNAL_ADMIN],
		{}
	),

	[CLIENT_PATHS.MARKETPLACE_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(MarketplacePage),
		CLIENT_PATHS.MARKETPLACE_PAGE,
		[FEATURES.INTERNAL_ADMIN],
		{}
	),

	[CLIENT_PATHS.MANAGE_DASHBOARDS_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(ManageDashboardPage),
		CLIENT_PATHS.MANAGE_DASHBOARDS_PAGE,
		[FEATURES.MANAGE_DASHBOARDS],
		{ menuIcon: "Information" }
	),

	[CLIENT_PATHS.MANAGE_BANNER_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(ManageBannerPage),
		CLIENT_PATHS.MANAGE_BANNER_PAGE,
		[FEATURES.INTERNAL_ADMIN],
		{ menuIcon: "Bell" }
	)
};

const partnerManagerPages: PageMetadatas = {
	[CLIENT_PATHS.MANAGE_SAAS_USERS_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(ManageSaasUsersPage),
		CLIENT_PATHS.MANAGE_SAAS_USERS_PAGE,
		[FEATURES.MANAGE_EXTERNAL_USERS],
		{ menuIcon: "UserMultiuser" }
	),
	[CLIENT_PATHS.MANAGE_SAAS_USER_INVITATIONS_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(ManageSaasUserInvitationsPage),
		CLIENT_PATHS.MANAGE_SAAS_USER_INVITATIONS_PAGE,
		[FEATURES.MANAGE_EXTERNAL_USERS]
	),
	[CLIENT_PATHS.INVITE_SAAS_USER_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(InviteSaasUserPage),
		CLIENT_PATHS.INVITE_SAAS_USER_PAGE,
		[FEATURES.MANAGE_EXTERNAL_USERS]
	),
	[CLIENT_PATHS.EDIT_SAAS_USER_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(EditSaasUserPage),
		CLIENT_PATHS.EDIT_SAAS_USER_PAGE,
		[FEATURES.MANAGE_EXTERNAL_USERS]
	)
};

const marketingPages: PageMetadatas = {
	[CLIENT_PATHS.MARKETING_SELECT_PARTNER_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(MarketingSelectPartnerPage),
		CLIENT_PATHS.MARKETING_SELECT_PARTNER_PAGE,
		[FEATURES.MANAGE_MARKETING_CONTENT],
		{ menuIcon: "Megaphone" }
	),
	[CLIENT_PATHS.MANAGE_MARKETING_CONTENT_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(ManageMarketingContentPage),
		CLIENT_PATHS.MANAGE_MARKETING_CONTENT_PAGE,
		[FEATURES.MANAGE_MARKETING_CONTENT]
	)
};

// TODO: FIGURE OUT HOW TO HANDLE THESE IN NEW PARADIGM
const operationPages: PageMetadatas = {
	[CLIENT_PATHS.SELECT_OPERATIONAL_FORM_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(SelectOperationalFormPage),
		CLIENT_PATHS.SELECT_OPERATIONAL_FORM_PAGE,
		[FEATURES.INTERNAL_OPERATIONS_TEAM],
		{ menuIcon: "MailInbox" }
	),
	[CLIENT_PATHS.CUSTOMER_INVOICE_FORM_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(CustomerInvoiceFormPage),
		CLIENT_PATHS.CUSTOMER_INVOICE_FORM_PAGE,
		[FEATURES.INTERNAL_OPERATIONS_TEAM],
		{}
	),
	[CLIENT_PATHS.CREATE_CUSTOMER_INVOICE_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(CreateCustomerInvoicePage),
		CLIENT_PATHS.CREATE_CUSTOMER_INVOICE_PAGE,
		[FEATURES.INTERNAL_OPERATIONS_TEAM],
		{}
	),
	[CLIENT_PATHS.EDIT_CUSTOMER_INVOICE_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(EditCustomerInvoicePage),
		CLIENT_PATHS.EDIT_CUSTOMER_INVOICE_PAGE,
		[FEATURES.INTERNAL_OPERATIONS_TEAM],
		{}
	),
	[CLIENT_PATHS.WALMART_DISPUTE_FORM_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(WalmartDisputesFormPage),
		CLIENT_PATHS.WALMART_DISPUTE_FORM_PAGE,
		[FEATURES.INTERNAL_OPERATIONS_TEAM],
		{}
	),
	[CLIENT_PATHS.CREATE_WALMART_DISPUTE_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(CreateWalmartDisputesPage),
		CLIENT_PATHS.CREATE_WALMART_DISPUTE_PAGE,
		[FEATURES.INTERNAL_OPERATIONS_TEAM],
		{}
	),
	[CLIENT_PATHS.EDIT_WALMART_DISPUTE_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(EditWalmartDisputesPage),
		CLIENT_PATHS.EDIT_WALMART_DISPUTE_PAGE,
		[FEATURES.INTERNAL_OPERATIONS_TEAM],
		{}
	)
};

const supportPages: PageMetadatas = {
	[CLIENT_PATHS.FREQUENTLY_ASKED_QUESTIONS_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(FrequentlyAskedQuestionsPage),
		CLIENT_PATHS.FREQUENTLY_ASKED_QUESTIONS_PAGE,
		[FEATURES.NO_FEATURE_REQUIRED],
		{ menuIcon: "Question" }
	),
	[CLIENT_PATHS.REPORT_AN_ISSUE_PAGE]: new PageMetadata(
		legacyOrNotAuthorized(ReportAnIssuePage),
		CLIENT_PATHS.REPORT_AN_ISSUE_PAGE,
		[FEATURES.NO_FEATURE_REQUIRED],
		{ menuIcon: "MailEnvelope" }
	),
	[CLIENT_PATHS.LOGOUT]: new PageMetadata(
		LogoutPage,
		CLIENT_PATHS.LOGOUT,
		[FEATURES.NO_FEATURE_REQUIRED],
		{ menuIcon: "Lock" }
	),
	[CLIENT_PATHS.LOGIN]: new PageMetadata(
		LoginPage,
		CLIENT_PATHS.LOGIN, 
		[FEATURES.NO_FEATURE_REQUIRED]
	)
};

const nonAdminPages: PageMetadatas = {
	...corePages,
	...supportPages
};

// @ts-ignore
export const pageMetadatas: Required<PageMetadatas> = {
	...nonAdminPages,
	...operationPages,
	...adminPages,
	...partnerManagerPages,
	...marketingPages
};
